import React, { useEffect, useState } from 'react'
import {
  Button, Col, Form, Input, Modal, Row, Select, message,
} from 'antd'

import type { CreateLinkedAccountPayload, LinkedAccountType } from '../../types/linkedAccount'
import { createLinkedAccount } from '../../apis/linkedAccountApis'
import { isForbiddenError } from '../../util/linkedAccount/linkedAccountErrors'
import { useIsMobile } from '../../hook/useMediaQuery'
import FieldErrorText from './FieldErrorText'
import { renderPasswordVisibilityIcon } from './passwordVisibilityIcon'
import {
  LINKED_ACCOUNT_FULLSCREEN_MODAL_CLASS,
  LINKED_ACCOUNT_FULLSCREEN_MODAL_STYLE,
  LINKED_ACCOUNT_STACKED_FORM_CLASS,
  LINKED_ACCOUNT_DIALOG_CLASS,
  LINKED_ACCOUNT_FORM_CLASS,
  LINKED_ACCOUNT_NOTE_AUTO_SIZE,
  LINKED_ACCOUNT_FORM_MODAL_WIDTH_PX,
} from '../../constants/linkedAccount'

/**
 * Hộp thoại "Thêm tài khoản liên kết" (SP-005, Task 5) — Figma `26:8643`, biến thể "Khác" `26:8816`,
 * biến thể lỗi `26:8989`.
 *
 * 🔴 KHÔNG dùng `isNotFoundError` ở luồng lỗi của hộp thoại này. Endpoint tạo có mã 404 riêng của nó
 * (`E10001: User doesn't exists` — tài khoản ERP đang cấp không còn tồn tại), khác hẳn nghĩa "bản
 * ghi đang sửa đã bị xoá, làm mới danh sách" mà `isNotFoundError` phục vụ ở các hộp thoại khác của
 * cụm này (`RevealPasswordModal`). Gộp chung sẽ bảo người dùng làm mới một danh sách không liên
 * quan gì tới lỗi thật. 403 được bắt riêng bằng `isForbiddenError`; MỌI lỗi khác (kể cả 404, 500…)
 * đều rơi vào câu lỗi chung.
 *
 * 🔴 Mật khẩu KHÔNG áp chính sách mật khẩu Simplus (`A-06`) — chỉ cần khác rỗng. KHÔNG trim mật
 * khẩu (khoảng trắng hai đầu là một phần giá trị carrier cấp). `username`/`custom_type_name` thì
 * trim trước khi gửi.
 *
 * DOM hook cho bộ test trình duyệt:
 * - `.add-linked-account-modal` — className của `Modal` (render qua PORTAL vào `document.body`).
 * - `.add-linked-account-modal__account-type-item` — `Form.Item` bọc Select "Loại tài khoản";
 *   `.add-linked-account-modal__account-type-select .ant-select-selector` để mở dropdown.
 * - `.add-linked-account-modal__custom-type-item` — `Form.Item` "Tên loại tài khoản", CHỈ có mặt
 *   trong DOM khi loại đang chọn là `other`; `.add-linked-account-modal__custom-type-input` là ô.
 * - `.add-linked-account-modal__username-item` / `__username-input` — "Tên tài khoản".
 * - `.add-linked-account-modal__password-item` / `__password-input` (bọc ngoài `Input.Password`,
 *   ô thật nằm ở `__password-input input`, cùng khuôn `PasswordPromptDialog`).
 * - `.add-linked-account-modal__note-item` / `__note-input` — "Ghi chú" (`Input.TextArea`).
 * - `.add-linked-account-modal__cancel-btn` / `__submit-btn` — hai nút footer.
 * - Lỗi từng trường hiện qua `.ant-form-item-explain-error` bên trong đúng `Form.Item` ở trên
 *   (hành vi mặc định của antd `Form` + `rules`, không tự dựng UI lỗi riêng).
 *
 * Lưới: MỘT `Row` duy nhất, các ô xếp theo dòng chảy 2 cột — Figma `26:8643` (chưa chọn loại) là
 * `[Loại | Tên tài khoản] [Mật khẩu]`, còn `26:8816` (Khác) là `[Loại | Tên loại] [Tên | Mật khẩu]`.
 * KHÔNG chia `Row` cố định theo hàng (bản cũ chừa trống ô bên phải "Loại" khi chưa chọn Khác).
 *
 * 🔴 Responsive ≤767px (SP-005, Task 9): `useIsMobile()` quyết định `width`/`style` của `Modal` —
 * `'100vw'` + `LINKED_ACCOUNT_FULLSCREEN_MODAL_STYLE` (hằng module-level, `constants/linkedAccount.ts`)
 * và thêm class `LINKED_ACCOUNT_FULLSCREEN_MODAL_CLASS`. Lưới 2 cột (`Row`/`Col span={12}`) xuống
 * 1 cột bằng class `LINKED_ACCOUNT_STACKED_FORM_CLASS` trên chính `<Form>` — SCSS override
 * `.ant-col-12` bên trong lớp đó, không đổi JSX của từng `Col`. antd `Modal` đặt `className`/`width`
 * THẲNG lên `.ant-modal` (khác bẫy `Drawer`), nên không cần `styles={{ wrapper }}`.
 */

export interface AddLinkedAccountModalProps {
  open: boolean
  /** Tài khoản ERP đang được cấp. Đối tác truyền `representative_id`. */
  userId: string
  onCancel: () => void
  /** Gọi sau khi tạo xong — nơi gọi tải lại danh sách. */
  onCreated: () => void
}

interface AddLinkedAccountFormValues {
  account_type?: LinkedAccountType
  custom_type_name?: string
  username?: string
  password?: string
  note?: string
}

const MODAL_TITLE = 'Thêm tài khoản liên kết'
const CANCEL_LABEL = 'Huỷ'
const SAVE_LABEL = 'Lưu'

const ACCOUNT_TYPE_LABEL = 'Loại tài khoản'
const CUSTOM_TYPE_NAME_LABEL = 'Tên loại tài khoản'
const USERNAME_LABEL = 'Tên tài khoản'
const PASSWORD_LABEL = 'Mật khẩu'
const NOTE_LABEL = 'Ghi chú'

const ACCOUNT_TYPE_PLACEHOLDER = 'Chọn loại tài khoản'
const CUSTOM_TYPE_NAME_PLACEHOLDER = 'Nhập tên loại'
const USERNAME_PLACEHOLDER = 'Nhập tên tài khoản'
const PASSWORD_PLACEHOLDER = 'Nhập mật khẩu'
const NOTE_PLACEHOLDER = 'Nhập ghi chú'

const ACCOUNT_TYPE_REQUIRED_MESSAGE = 'Vui lòng chọn loại tài khoản'
const USERNAME_REQUIRED_MESSAGE = 'Vui lòng nhập tên tài khoản'
const PASSWORD_REQUIRED_MESSAGE = 'Vui lòng nhập mật khẩu'
const CUSTOM_TYPE_NAME_REQUIRED_MESSAGE = 'Vui lòng nhập tên loại tài khoản'

const SAVE_SUCCESS_MESSAGE = 'Đã lưu thông tin tài khoản'
const FORBIDDEN_MESSAGE = 'Bạn không có quyền thực hiện thao tác này.'
const GENERIC_ERROR_MESSAGE = 'Không lưu được tài khoản liên kết. Vui lòng thử lại.'

/** Trần ký tự — hợp đồng BE §2: username/password/custom_type_name ≤255, note ≤2000. */
const SHORT_FIELD_MAX_LENGTH = 255
const NOTE_MAX_LENGTH = 2000

const ACCOUNT_TYPE_OPTIONS: Array<{ value: LinkedAccountType; label: string }> = [
  { value: 'vinaphone', label: 'Vinaphone' },
  { value: 'viettel', label: 'Viettel' },
  { value: 'other', label: 'Khác' },
]

const AddLinkedAccountModal = ({
  open, userId, onCancel, onCreated,
}: AddLinkedAccountModalProps): JSX.Element => {
  const [form] = Form.useForm<AddLinkedAccountFormValues>()
  const [submitting, setSubmitting] = useState(false)
  const accountType = Form.useWatch('account_type', form)
  const isMobile = useIsMobile()

  // Mỗi lần MỞ ⇒ mọi trường trống, không giữ giá trị lần trước (kể cả sau khi lưu thành công,
  // parent đóng rồi mở lại KHÔNG unmount component này — `form` là instance sống xuyên suốt).
  useEffect(() => {
    if (open) form.resetFields()
  }, [open, form])

  // Chọn lại Vinaphone/Viettel ⇒ ẩn ô "Tên loại tài khoản" VÀ xoá giá trị đã gõ — không để rác
  // trong form store rồi lỡ gửi lên BE nếu sau này có đường nào đó đọc trực tiếp store.
  const handleValuesChange = (changedValues: Partial<AddLinkedAccountFormValues>): void => {
    if ('account_type' in changedValues && changedValues.account_type !== 'other') {
      form.setFieldsValue({ custom_type_name: undefined })
    }
  }

  const handleFinish = async (values: AddLinkedAccountFormValues): Promise<void> => {
    if (submitting) return
    // Form đã validate các trường bắt buộc trước khi gọi onFinish — kiểm tra lại ở đây chỉ để TS
    // thu hẹp kiểu từ optional (khai theo `AddLinkedAccountFormValues`) sang bắt buộc, không phải
    // một lượt validate thứ hai.
    if (values.account_type === undefined || values.username === undefined || values.password === undefined) {
      return
    }

    const payload: CreateLinkedAccountPayload = {
      account_type: values.account_type,
      username: values.username.trim(),
      // 🔴 KHÔNG trim — khoảng trắng hai đầu là một phần giá trị mật khẩu carrier cấp (A-06).
      password: values.password,
    }
    if (values.account_type === 'other') {
      payload.custom_type_name = (values.custom_type_name ?? '').trim()
    }
    const noteValue = values.note ?? ''
    if (noteValue !== '') {
      payload.note = noteValue
    }

    setSubmitting(true)
    try {
      await createLinkedAccount(userId, payload)
      message.success(SAVE_SUCCESS_MESSAGE)
      form.resetFields()
      onCreated()
      // 🔴 `onCancel` dùng làm tín hiệu "hãy đóng tôi lại" chung chung ở đây, KHÔNG mang nghĩa
      // "người dùng bấm Huỷ" — prop surface (theo brief) không có callback đóng riêng cho nhánh
      // thành công. Nơi gọi (container, chưa viết) truyền một handler CHỈ đóng dialog; không được
      // nhét hành vi đặc thù cho việc huỷ (hỏi xác nhận bỏ dở, phân biệt cancel/save trong
      // analytics…) vào handler đó, vì nhánh lưu thành công cũng đi qua đúng con đường này.
      onCancel()
    } catch (error) {
      if (isForbiddenError(error)) {
        message.error(FORBIDDEN_MESSAGE)
      } else {
        message.error(GENERIC_ERROR_MESSAGE)
      }
    } finally {
      setSubmitting(false)
    }
  }

  const handleSave = (): void => {
    form.submit()
  }

  return (
    <Modal
      className={`${LINKED_ACCOUNT_DIALOG_CLASS} add-linked-account-modal${isMobile ? ` ${LINKED_ACCOUNT_FULLSCREEN_MODAL_CLASS}` : ''}`}
      open={open}
      width={isMobile ? '100vw' : LINKED_ACCOUNT_FORM_MODAL_WIDTH_PX}
      style={isMobile ? LINKED_ACCOUNT_FULLSCREEN_MODAL_STYLE : undefined}
      title={MODAL_TITLE}
      maskClosable={false}
      destroyOnClose
      onCancel={onCancel}
      footer={[
        <Button key="cancel" size="large" className="add-linked-account-modal__cancel-btn" onClick={onCancel}>
          {CANCEL_LABEL}
        </Button>,
        <Button
          key="submit"
          type="primary"
          size="large"
          className="add-linked-account-modal__submit-btn"
          loading={submitting}
          disabled={submitting}
          onClick={handleSave}
        >
          {SAVE_LABEL}
        </Button>,
      ]}
    >
      <Form<AddLinkedAccountFormValues>
        form={form}
        layout="vertical"
        colon={false}
        // `large` của antd = ĐÚNG thông số Figma cho ô nhập (cao 40, chữ 16px, bo 8) — không tự đè CSS.
        size="large"
        className={isMobile ? `${LINKED_ACCOUNT_FORM_CLASS} ${LINKED_ACCOUNT_STACKED_FORM_CLASS}` : LINKED_ACCOUNT_FORM_CLASS}
        onFinish={handleFinish}
        onValuesChange={handleValuesChange}
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="account_type"
              label={ACCOUNT_TYPE_LABEL}
              className="add-linked-account-modal__account-type-item"
              rules={[{ required: true, message: <FieldErrorText text={ACCOUNT_TYPE_REQUIRED_MESSAGE} /> }]}
            >
              <Select
                className="add-linked-account-modal__account-type-select"
                placeholder={ACCOUNT_TYPE_PLACEHOLDER}
                options={ACCOUNT_TYPE_OPTIONS}
              />
            </Form.Item>
          </Col>
          {accountType === 'other' && (
            <Col span={12}>
              <Form.Item
                name="custom_type_name"
                label={CUSTOM_TYPE_NAME_LABEL}
                className="add-linked-account-modal__custom-type-item"
                rules={[{
                  required: true, whitespace: true, message: <FieldErrorText text={CUSTOM_TYPE_NAME_REQUIRED_MESSAGE} />,
                }]}
              >
                <Input
                  className="add-linked-account-modal__custom-type-input"
                  placeholder={CUSTOM_TYPE_NAME_PLACEHOLDER}
                  maxLength={SHORT_FIELD_MAX_LENGTH}
                />
              </Form.Item>
            </Col>
          )}
          <Col span={12}>
            <Form.Item
              name="username"
              label={USERNAME_LABEL}
              className="add-linked-account-modal__username-item"
              rules={[{ required: true, whitespace: true, message: <FieldErrorText text={USERNAME_REQUIRED_MESSAGE} /> }]}
            >
              <Input
                className="add-linked-account-modal__username-input"
                placeholder={USERNAME_PLACEHOLDER}
                maxLength={SHORT_FIELD_MAX_LENGTH}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="password"
              label={PASSWORD_LABEL}
              className="add-linked-account-modal__password-item"
              // 🔴 CỐ Ý không có `whitespace: true` ở đây (khác hẳn username/custom_type_name) —
              // mật khẩu carrier cấp có thể hợp lệ toàn khoảng trắng hoặc có khoảng trắng hai đầu
              // mang ý nghĩa, và A-06 cấm áp chính sách mật khẩu Simplus lên trường này. Chỉ cần
              // khác chuỗi rỗng là đủ (đúng hành vi mặc định của `required` cho kiểu string).
              rules={[{ required: true, message: <FieldErrorText text={PASSWORD_REQUIRED_MESSAGE} /> }]}
            >
              <Input.Password
                // 20/09/2026 — BẬT icon con mắt theo yêu cầu người dùng (Figma không vẽ). Ô này
                // chứa mật khẩu carrier do chính quản trị viên gõ vào, chưa từng rời trình duyệt,
                // nên cho hiện lại để soát chính tả không đụng ràng buộc bảo mật nào của SP-005.
                iconRender={renderPasswordVisibilityIcon}
                className="add-linked-account-modal__password-input"
                placeholder={PASSWORD_PLACEHOLDER}
                maxLength={SHORT_FIELD_MAX_LENGTH}
              />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item
              name="note"
              label={NOTE_LABEL}
              className="add-linked-account-modal__note-item"
            >
              <Input.TextArea
                className="add-linked-account-modal__note-input"
                placeholder={NOTE_PLACEHOLDER}
                autoSize={LINKED_ACCOUNT_NOTE_AUTO_SIZE}
                maxLength={NOTE_MAX_LENGTH}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  )
}

export default AddLinkedAccountModal
